import {
  Children,
  createContext,
  type MouseEvent,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

interface RouterContextValue {
  pathname: string;
  search: string;
  hash: string;
  navigate: (
    to: string,
    options?: { replace?: boolean; preventScrollReset?: boolean },
  ) => void;
}

const RouterContext = createContext<RouterContextValue | null>(null);
const ParamsContext = createContext<Record<string, string>>({});

function currentLocation(): Pick<RouterContextValue, "pathname" | "search" | "hash"> {
  return {
    pathname: window.location.pathname || "/",
    search: window.location.search,
    hash: window.location.hash,
  };
}

function useRouter(): RouterContextValue {
  const context = useContext(RouterContext);
  if (!context) throw new Error("Le routeur doit entourer l’application.");
  return context;
}

export function BrowserRouter({ children }: PropsWithChildren) {
  const [location, setLocation] = useState(currentLocation);

  useEffect(() => {
    const handlePopState = () => setLocation(currentLocation());
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigate = useCallback(
    (
      to: string,
      options?: { replace?: boolean; preventScrollReset?: boolean },
    ) => {
      const target = new URL(to, window.location.href);
      if (target.origin !== window.location.origin) {
        window.location.assign(target.href);
        return;
      }
      const next = `${target.pathname}${target.search}${target.hash}`;
      if (options?.replace) window.history.replaceState({}, "", next);
      else window.history.pushState({}, "", next);
      setLocation({
        pathname: target.pathname,
        search: target.search,
        hash: target.hash,
      });
      if (!options?.preventScrollReset) {
        window.scrollTo({ top: 0, left: 0 });
      }
    },
    [],
  );

  const value = useMemo(
    () => ({ ...location, navigate }),
    [location, navigate],
  );
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

interface LinkProps extends Omit<React.ComponentPropsWithoutRef<"a">, "href"> {
  to: string;
}

export function Link({ to, onClick, ...props }: LinkProps) {
  const { navigate } = useRouter();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.currentTarget.target === "_blank" ||
      event.currentTarget.hasAttribute("download")
    ) {
      return;
    }

    const target = new URL(to, window.location.href);
    if (target.origin !== window.location.origin) return;
    event.preventDefault();
    navigate(to);
  };

  return <a {...props} href={to} onClick={handleClick} />;
}

export function useLocation(): Pick<RouterContextValue, "pathname" | "search" | "hash"> {
  const { pathname, search, hash } = useRouter();
  return { pathname, search, hash };
}

export function useNavigate(): RouterContextValue["navigate"] {
  return useRouter().navigate;
}

export function useParams<T extends Record<string, string | undefined> = Record<string, string>>() {
  return useContext(ParamsContext) as T;
}

interface RouteProps {
  path: string;
  element: ReactNode;
}

export function Route(_props: RouteProps) {
  return null;
}

function splitPath(value: string): string[] {
  return value
    .replace(/^\/+|\/+$/g, "")
    .split("/")
    .filter(Boolean);
}

function matchRoute(pattern: string, pathname: string): Record<string, string> | null {
  if (pattern === "*") return {};
  const patternParts = splitPath(pattern);
  const pathParts = splitPath(pathname);
  if (patternParts.length !== pathParts.length) return null;

  const params: Record<string, string> = {};
  for (let index = 0; index < patternParts.length; index += 1) {
    const expected = patternParts[index];
    const actual = pathParts[index];
    if (expected.startsWith(":")) {
      try {
        params[expected.slice(1)] = decodeURIComponent(actual);
      } catch {
        return null;
      }
    } else if (expected !== actual) {
      return null;
    }
  }
  return params;
}

export function Routes({ children }: { children: ReactNode }) {
  const { pathname } = useRouter();
  const routes = Children.toArray(children).filter(isValidElement) as ReactElement<RouteProps>[];

  for (const route of routes) {
    const params = matchRoute(route.props.path, pathname);
    if (params !== null) {
      return <ParamsContext.Provider value={params}>{route.props.element}</ParamsContext.Provider>;
    }
  }
  return null;
}
